import * as React from "react";
import {
    Button,
    Input,
    Popover,
    PopoverTrigger,
    PopoverSurface,
    Toast,
    ToastTitle,
    useId,
    useToastController,
    Toaster,
} from "@fluentui/react-components";
import { ClockBill20Regular } from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";

type AvailabilityPopoverProps = {
    contactId: number;
    initialValue: string | null | undefined;
    onUpdate: (newValue: string) => void;
};

export const AvailabilityPopover: React.FC<AvailabilityPopoverProps> = ({
    contactId,
    initialValue,
    onUpdate,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [value, setValue] = React.useState(initialValue || "");
    const [isSaving, setIsSaving] = React.useState(false);

    const toasterId = useId("availability-toaster");
    const { dispatchToast } = useToastController(toasterId);

    React.useEffect(() => {
        setValue(initialValue || "");
    }, [initialValue]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const response = await fetch(apiUrl(`contact/${contactId}`), {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ erreichbarkeit: value }),
            });

            if (response.ok) {
                onUpdate(value);
                setIsOpen(false);
                dispatchToast(
                    <Toast><ToastTitle>Erreichbarkeit gespeichert</ToastTitle></Toast>,
                    { intent: "success" }
                );
            } else {
                throw new Error("Failed to save");
            }
        } catch (error) {
            console.error("Error saving erreichbarkeit", error);
            dispatchToast(
                <Toast><ToastTitle>Fehler beim Speichern</ToastTitle></Toast>,
                { intent: "error" }
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Toaster toasterId={toasterId} />
            <Popover open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)}>
                <PopoverTrigger disableButtonEnhancement>
                    <Button
                        icon={<ClockBill20Regular />}
                        appearance="subtle"
                        title={value || "Erreichbarkeit hinzufügen"}
                    />
                </PopoverTrigger>
                <PopoverSurface style={{ padding: "12px", width: "300px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <Input
                            value={value}
                            onChange={(_, data) => setValue(data.value)}
                            placeholder="Erreichbarkeit (z.B. Mo-Fr 9-17 Uhr)"
                            style={{ width: "100%" }}
                        />
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                            <Button size="small" onClick={() => setIsOpen(false)}>Abbrechen</Button>
                            <Button
                                size="small"
                                appearance="primary"
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? "Lädt..." : "Speichern"}
                            </Button>
                        </div>
                    </div>
                </PopoverSurface>
            </Popover>
        </>
    );
};
